import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchMe, getUsage, loginUser, registerUser, setToken, clearToken, getToken } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null = loading, false = anon, object = logged in
  const [usage, setUsage] = useState(null);

  const refreshUsage = useCallback(async () => {
    try { setUsage(await getUsage()); } catch { /* ignore */ }
  }, []);

  const loadMe = useCallback(async () => {
    if (!getToken()) { setUser(false); refreshUsage(); return; }
    try {
      const u = await fetchMe();
      setUser(u);
    } catch {
      clearToken();
      setUser(false);
    }
    refreshUsage();
  }, [refreshUsage]);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (email, password) => {
    const { token, user } = await loginUser(email, password);
    setToken(token); setUser(user); await refreshUsage();
    return user;
  };
  const register = async (email, password) => {
    const { token, user } = await registerUser(email, password);
    setToken(token); setUser(user); await refreshUsage();
    return user;
  };
  // Google flow: the backend hands us a finished app JWT in the redirect fragment.
  //
  // The token has to be stored before fetchMe, because fetchMe is what
  // validates it and the request interceptor reads it from storage. But it was
  // stored and then left there when that validation failed — an expired,
  // tampered or replayed fragment wrote a token the app then attached to every
  // subsequent request, forever, while showing a signed-out UI. loadMe above
  // has always cleared on exactly this failure; the two agree now.
  const loginWithToken = async (token) => {
    setToken(token);
    try {
      const u = await fetchMe();
      setUser(u);
      await refreshUsage();
      return u;
    } catch (e) {
      clearToken();
      setUser(false);
      // Rethrown, not swallowed: the callback route shows an error and sends
      // them back to sign in, and it can only do that if it hears about this.
      throw e;
    }
  };
  const logout = () => { clearToken(); setUser(false); refreshUsage(); };

  const isPro = !!user && user.plan === "pro";
  const isFree = !!user && user.plan === "free";

  return (
    <AuthCtx.Provider value={{ user, usage, isPro, isFree, login, register, loginWithToken, logout, refreshUsage, reloadUser: loadMe }}>
      {children}
    </AuthCtx.Provider>
  );
}
